// Каталог инструмента — CRUD над `/api/public/tool`. Список таблицей,
// создание/редактирование отдельным экраном. Без оптимистичных обновлений:
// каждый успех тянет refetch (список короткий — десятки строк).
//
// Per-kind схема полей — в `tool_kinds.ts`. Editor показывает основные
// поля (D/d/L/m/...) + базовые подформы для tube-with-joins (joins) и
// bit (nozzles). Остальные поля (description вне основной формы, и т.п.)
// сохраняются «как есть» из payload — UI редактирует подмножество.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listTools,
  createTool,
  updateTool,
  deleteTool,
  ToolInUseError,
} from './api/tool'
import type { ToolDto } from './api/tool'
import {
  listDrillingCompanies,
  createDrillingCompany,
} from './api/drilling_company'
import type { DrillingCompanyDto } from './api/drilling_company'
import {
  TOOL_KINDS,
  KIND_BY_ID,
  kindLabel,
  scaffoldPayload,
  unwrapPayload,
  wrapPayload,
  fromSi,
  toSi,
} from './tool_kinds'
import type { ToolKindMeta, ToolField } from './tool_kinds'
import type { ToolKind } from './tool_kind'

// ────────────────────── редактор ──────────────────────

function toolAssetSrc(kind: ToolKindMeta): string {
  return `${import.meta.env.BASE_URL}${kind.asset.replace(/^\//, '')}`
}

const ToolKindGlyph: Component<{ kind: ToolKindMeta }> = (props) => {
  const glyph = () => {
    switch (props.kind.id) {
      case 'pdc_bit':
        return (
          <>
            <rect class="dark" x="40" y="4" width="16" height="14" rx="3" />
            <path class="body" d="M34 18h28l6 14-8 18H36l-8-18z" />
            <path class="cutters" d="M48 21l5 12 12-2-9 9 4 9-12-5-12 5 4-9-9-9 12 2z" />
            <circle class="accent-fill" cx="48" cy="34" r="5" />
          </>
        )
      case 'tricone_bit':
        return (
          <>
            <rect class="dark" x="41" y="4" width="14" height="14" rx="3" />
            <path class="body" d="M35 18h26l5 12-7 18H37l-7-18z" />
            <circle class="cone" cx="40" cy="35" r="8" />
            <circle class="cone" cx="56" cy="35" r="8" />
            <circle class="cone" cx="48" cy="42" r="8" />
            <path class="line" d="M36 26h24M39 32h18M42 39h12" />
          </>
        )
      case 'dp':
        return (
          <>
            <rect class="steel" x="15" y="26" width="66" height="4" rx="2" />
            <rect class="dark" x="10" y="23" width="10" height="10" rx="2" />
            <rect class="dark" x="76" y="23" width="10" height="10" rx="2" />
          </>
        )
      case 'dc':
      case 'hmdc':
        return (
          <>
            <rect class="body" x="15" y="22" width="66" height="12" rx="6" />
            <rect class="hollow" x="20" y="26" width="56" height="4" rx="2" />
            <Show when={props.kind.id === 'hmdc'}>
              <path class="line" d="M25 23h46M25 33h46" />
            </Show>
          </>
        )
      case 'hwdp1':
      case 'hwdp2':
        return (
          <>
            <rect class="steel" x="12" y="26" width="72" height="4" rx="2" />
            <rect class="body" x="18" y="21" width="15" height="14" rx="3" />
            <rect class="body" x="63" y="21" width="15" height="14" rx="3" />
            <Show when={props.kind.id === 'hwdp2'}>
              <rect class="dark" x="40" y="24" width="16" height="8" rx="2" />
            </Show>
          </>
        )
      case 'casing':
        return (
          <>
            <rect class="steel" x="11" y="23" width="74" height="10" rx="5" />
            <rect class="hollow" x="16" y="26" width="64" height="4" rx="2" />
            <rect class="dark" x="78" y="21" width="7" height="14" rx="2" />
          </>
        )
      case 'tj':
        return (
          <>
            <rect class="steel" x="16" y="24" width="64" height="8" rx="4" />
            <rect class="dark" x="31" y="18" width="34" height="20" rx="2" />
            <path class="line" d="M36 21v14M43 21v14M50 21v14M57 21v14" />
          </>
        )
      case 'stabilizer':
      case 'reamer':
        return (
          <>
            <rect class="steel" x="14" y="26" width="68" height="4" rx="2" />
            <rect class="body" x="31" y="17" width="34" height="22" rx="4" />
            <path class="accent-fill" d="M31 18l-8 7v6l8 7zM65 18l8 7v6l-8 7z" />
            <Show when={props.kind.id === 'reamer'}>
              <path class="cutters" d="M36 20h24l4 8-4 8H36l-4-8z" />
            </Show>
          </>
        )
      case 'pdm':
        return (
          <>
            <rect class="steel" x="14" y="26" width="68" height="4" rx="2" />
            <path class="dark" d="M20 21h10l3 14H17z" />
            <path class="dark" d="M66 21h10l3 14H63z" />
          </>
        )
      case 'dv_collar':
        // Муфта МСЦ — обсадная секция с окнами разрядки (два порта на теле).
        return (
          <>
            <rect class="steel" x="13" y="22" width="70" height="12" rx="4" />
            <rect class="hollow" x="18" y="26" width="60" height="4" rx="2" />
            <circle class="accent-fill" cx="38" cy="28" r="3" />
            <circle class="accent-fill" cx="58" cy="28" r="3" />
            <rect class="dark" x="9" y="20" width="6" height="16" rx="2" />
            <rect class="dark" x="81" y="20" width="6" height="16" rx="2" />
          </>
        )
      case 'packer':
        // Пакер — труба с резиновой манжетой (утолщение в середине).
        return (
          <>
            <rect class="steel" x="12" y="24" width="72" height="8" rx="3" />
            <rect class="hollow" x="16" y="26" width="64" height="4" rx="2" />
            <rect class="accent-fill" x="36" y="17" width="24" height="22" rx="4" />
            <path class="line" d="M40 19v18M48 19v18M56 19v18" />
          </>
        )

      default:
        return (
          <>
            <rect class="steel" x="16" y="25" width="64" height="6" rx="3" />
            <rect class="dark" x="12" y="23" width="8" height="10" rx="2" />
            <rect class="dark" x="76" y="23" width="8" height="10" rx="2" />
          </>
        )
    }
  }

  return (
    <svg class="equipment-kind-glyph" viewBox="0 0 96 56" role="img" aria-label={props.kind.label}>
      {glyph()}
    </svg>
  )
}

/// Состояние редактора. payloadBody — полный распакованный body инструмента
/// (без `<Tag>` обёртки). Поля формы держим как строки (текстовые инпуты).
/// Сохраняем все ключи payloadBody — даже те, что UI не показывает (id,
/// description, joins, nozzles, и т.п.); editor правит подмножество и
/// при submit отсылает обратно полный объект.
type EditorState = {
  /// `null` — создаём новый. Иначе редактируем существующий.
  source: ToolDto | null
  kindId: ToolKind
  title: string
  /// description хранится в payloadBody, но отображается в форме как
  /// отдельное верхнеуровневое поле (UX). При submit пишется обратно в payloadBody.
  description: string
  /// Все основные поля как строки (текстовые поля).
  fieldStrs: Record<string, string>
  /// joins-подформа (для tube_with_joins). Поля joins — все диаметры (m) и длины (m).
  joinsStrs: Record<string, string>
  /// nozzles-подформа (для bit). Каждая насадка — { diam (mm), quantity, mu_coef }.
  nozzles: Array<{ diam: string; quantity: string; mu_coef: string }>
  /// Полный payloadBody для сохранения «как есть» полей вне UI.
  rawBody: Record<string, unknown>
  /// Бурильная компания (uuid) или `null` — без привязки.
  drillingCompanyId: string | null
  /// Теги — редактируемый набор (сервер нормализует trim+lowercase). Храним
  /// как введено; дедуп/нормализацию делает бэк.
  tags: string[]
}

/// Сравнение наборов тегов как последовательностей (порядок от сервера
/// стабилен — алфавит). Для dirty-проверки.
function sameTags(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  return a.every((t, i) => t === b[i])
}

/// Нормализовать тег под клиентский показ/дедуп: trim + lowercase (зеркало
/// серверной нормализации). Пустой → null (не добавляем).
function normTag(raw: string): string | null {
  const t = raw.trim().toLowerCase()
  return t.length > 0 ? t : null
}

function buildState(kindId: ToolKind, source: ToolDto | null, body: Record<string, unknown>): EditorState {
  const meta = KIND_BY_ID[kindId]
  const fieldStrs: Record<string, string> = {}
  for (const f of meta.fields) {
    const raw = body[f.key]
    const num = typeof raw === 'number' ? raw : 0
    fieldStrs[f.key] = String(fromSi(num, f.unit))
  }
  const joinsStrs: Record<string, string> = {}
  if (meta.shape === 'tube_with_joins' && body.joins && typeof body.joins === 'object') {
    const j = body.joins as Record<string, unknown>
    for (const k of Object.keys(j)) {
      const v = typeof j[k] === 'number' ? (j[k] as number) : 0
      // outer_*_D* и inner_*_D* — диаметры (mm display); l1..l6 — длины (m display).
      const isDiam = /_D\d$/.test(k)
      joinsStrs[k] = String(isDiam ? v * 1000.0 : v)
    }
  }
  const nozzles: EditorState['nozzles'] = []
  if (meta.shape === 'bit' && body.nozzles && typeof body.nozzles === 'object') {
    const n = body.nozzles as { items?: Array<Record<string, unknown>> }
    for (const item of n.items ?? []) {
      const diam = typeof item.diam === 'number' ? item.diam : 0
      const quantity = typeof item.quantity === 'number' ? item.quantity : 0
      const mu = typeof item.mu_coef === 'number' ? item.mu_coef : 0
      nozzles.push({ diam: String(diam * 1000.0), quantity: String(quantity), mu_coef: String(mu) })
    }
  }
  const description = typeof body.description === 'string' ? body.description : ''
  return {
    source,
    kindId,
    title: source?.title ?? '',
    description,
    fieldStrs,
    joinsStrs,
    nozzles,
    rawBody: body,
    drillingCompanyId: source?.drilling_company_id ?? null,
    tags: source?.tags ? [...source.tags] : [],
  }
}

function emptyEditor(kindId: ToolKind): EditorState {
  const payload = scaffoldPayload(kindId)
  const body = unwrapPayload(kindId, payload)!
  return buildState(kindId, null, body)
}

function editorFrom(source: ToolDto): EditorState {
  const body = unwrapPayload(source.kind, source.payload)
  if (!body) {
    // payload без ожидаемого тега — fallback на scaffold + сохранение полей,
    // чтобы юзер мог хотя бы пересохранить запись с дефолтами.
    const fallback = unwrapPayload(source.kind, scaffoldPayload(source.kind))!
    return buildState(source.kind, source, fallback)
  }
  return buildState(source.kind, source, body)
}

type EditorProps = {
  state:     EditorState
  companies: DrillingCompanyDto[]
  onClose:   () => void
  onSaved:   () => void
  // Создание новой компании на лету (при выборе «+ Новая…» в селекте).
  // Возвращает созданный объект; вызывающий обновляет список и подставляет id.
  onCreateCompany: (name: string) => Promise<DrillingCompanyDto>
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null
  const meta = (): ToolKindMeta => KIND_BY_ID[st().kindId]
  const assetSrc = () => toolAssetSrc(meta())

  // Dirty-проверка: что-то отличается от initial. Сравниваем строки, а не SI —
  // для UI важны видимые отличия.
  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      if (c.title.trim().length > 0) return true
      if (c.description.trim().length > 0) return true
      if (c.kindId !== initial.kindId) return true
      if (c.drillingCompanyId !== initial.drillingCompanyId) return true
      if (c.tags.length > 0) return true
      // Любое непустое значение в полях = dirty (на create все начинаются с дефолтов).
      // Точнее — отличие от initial.
      for (const f of meta().fields) {
        if ((c.fieldStrs[f.key] ?? '') !== (initial.fieldStrs[f.key] ?? '')) return true
      }
      return false
    }
    if (c.title !== initial.title) return true
    if (c.description !== initial.description) return true
    if (c.kindId !== initial.kindId) return true
    if (c.drillingCompanyId !== initial.drillingCompanyId) return true
    if (!sameTags(c.tags, initial.tags)) return true
    for (const f of meta().fields) {
      if ((c.fieldStrs[f.key] ?? '') !== (initial.fieldStrs[f.key] ?? '')) return true
    }
    if (meta().shape === 'tube_with_joins') {
      for (const k of Object.keys(c.joinsStrs)) {
        if ((c.joinsStrs[k] ?? '') !== (initial.joinsStrs[k] ?? '')) return true
      }
    }
    if (meta().shape === 'bit') {
      if (c.nozzles.length !== initial.nozzles.length) return true
      for (let i = 0; i < c.nozzles.length; i++) {
        const a = c.nozzles[i]!
        const b = initial.nozzles[i]
        if (!b) return true
        if (a.diam !== b.diam || a.quantity !== b.quantity || a.mu_coef !== b.mu_coef) return true
      }
    }
    return false
  })

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  const onChangeKind = (newKind: ToolKind) => {
    // Смена kind = пересоздать поля по новой схеме. Kind-независимое (title,
    // описание, компания, теги) переносим.
    const next = emptyEditor(newKind)
    setSt({
      ...next,
      title: st().title,
      description: st().description,
      source: st().source,
      drillingCompanyId: st().drillingCompanyId,
      tags: st().tags,
    })
  }

  const updateField = (key: string, val: string) =>
    setSt({ ...st(), fieldStrs: { ...st().fieldStrs, [key]: val } })

  const updateJoin = (key: string, val: string) =>
    setSt({ ...st(), joinsStrs: { ...st().joinsStrs, [key]: val } })

  const addNozzle = () =>
    setSt({ ...st(), nozzles: [...st().nozzles, { diam: '15', quantity: '1', mu_coef: '0.95' }] })

  const removeNozzle = (i: number) => {
    const next = [...st().nozzles]
    next.splice(i, 1)
    setSt({ ...st(), nozzles: next })
  }

  const updateNozzle = (i: number, field: 'diam' | 'quantity' | 'mu_coef', val: string) => {
    const next = [...st().nozzles]
    next[i] = { ...next[i]!, [field]: val }
    setSt({ ...st(), nozzles: next })
  }

  // ── теги ──
  const [tagInput, setTagInput] = createSignal('')

  const addTag = () => {
    const t = normTag(tagInput())
    setTagInput('')
    if (t === null) return
    if (st().tags.includes(t)) return // дубль — игнор (сервер бы схлопнул)
    setSt({ ...st(), tags: [...st().tags, t] })
  }

  const removeTag = (t: string) =>
    setSt({ ...st(), tags: st().tags.filter((x) => x !== t) })

  const parseFieldValue = (f: ToolField, raw: string): number | null => {
    const trimmed = raw.trim()
    if (trimmed === '') return 0
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return null
    return toSi(n, f.unit)
  }

  const buildPayloadBody = (cur: EditorState): { body: Record<string, unknown> } | { error: string } => {
    const body: Record<string, unknown> = { ...cur.rawBody, description: cur.description }
    for (const f of meta().fields) {
      const raw = cur.fieldStrs[f.key] ?? ''
      const v = parseFieldValue(f, raw)
      if (v === null) return { error: `Поле ${f.label}: ожидается число` }
      body[f.key] = v
    }
    if (meta().shape === 'tube_with_joins') {
      const joins: Record<string, number> = {}
      for (const k of Object.keys(cur.joinsStrs)) {
        const trimmed = (cur.joinsStrs[k] ?? '').trim()
        const n = trimmed === '' ? 0 : Number(trimmed)
        if (!Number.isFinite(n)) return { error: `joins.${k}: ожидается число` }
        const isDiam = /_D\d$/.test(k)
        joins[k] = isDiam ? n / 1000.0 : n
      }
      body.joins = joins
    }
    if (meta().shape === 'bit') {
      const items: Array<Record<string, number>> = []
      for (let i = 0; i < cur.nozzles.length; i++) {
        const nz = cur.nozzles[i]!
        const d = Number(nz.diam.trim() === '' ? '0' : nz.diam)
        const q = Number(nz.quantity.trim() === '' ? '0' : nz.quantity)
        const mu = Number(nz.mu_coef.trim() === '' ? '0' : nz.mu_coef)
        if (!Number.isFinite(d) || !Number.isFinite(q) || !Number.isFinite(mu)) {
          return { error: `Насадка #${i + 1}: ожидается число` }
        }
        items.push({ diam: d / 1000.0, quantity: Math.round(q), mu_coef: mu })
      }
      body.nozzles = { items }
    }
    return { body }
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const titleTrimmed = cur.title.trim()
    if (titleTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }

    const built = buildPayloadBody(cur)
    if ('error' in built) {
      setErr(built.error)
      return
    }
    const payload = wrapPayload(cur.kindId, built.body)

    setBusy(true)
    try {
      if (isCreate()) {
        await createTool({
          kind:    cur.kindId,
          title:   titleTrimmed,
          payload,
          drilling_company_id: cur.drillingCompanyId,
          tags:    cur.tags,
        })
      } else {
        // На update: если company осталась `null`, а раньше что-то было —
        // шлём явный clear. Иначе обычный COALESCE («None» = не трогать).
        const prev = cur.source!.drilling_company_id ?? null
        const clearCompany = cur.drillingCompanyId === null && prev !== null
        await updateTool(cur.source!.id, {
          kind:    cur.kindId,
          title:   titleTrimmed,
          payload,
          drilling_company_id: cur.drillingCompanyId,
          clear_drilling_company: clearCompany,
          // Полная замена набора тегов (пустой = очистить). origin НЕ шлём —
          // провенанс, переживает round-trip через «не трогать».
          tags:    cur.tags,
        })
      }
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новый инструмент' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать инструмент' : st().title || 'Инструмент'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <div class="equipment-editor-grid">
        <aside class="equipment-kind-panel">
          <div class="equipment-panel-title">Тип</div>
          <div class="equipment-kind-list">
            <For each={TOOL_KINDS}>
              {(k) => (
                <button
                  class={'equipment-kind-btn' + (st().kindId === k.id ? ' active' : '')}
                  type="button"
                  disabled={!isCreate()}
                  onClick={() => onChangeKind(k.id)}
                  title={k.label}
                >
                  <span class="equipment-kind-icon" aria-hidden="true">
                    <ToolKindGlyph kind={k} />
                    <span>{k.code}</span>
                  </span>
                  <span class="equipment-kind-name">{k.label}</span>
                </button>
              )}
            </For>
          </div>
          <Show when={!isCreate()}>
            <div class="field-hint equipment-lock-hint">Тип нельзя менять у существующей записи</div>
          </Show>
        </aside>

        <main class="equipment-form-panel">
          <section class="equipment-blueprint">
            <div class="equipment-blueprint-meta">
              <span class="badge accent">{meta().code}</span>
              <span>{meta().label}</span>
            </div>
            <div class="equipment-blueprint-stage">
              <img src={assetSrc()} alt={`Чертеж: ${meta().label}`} />
            </div>
          </section>

          <section class="equipment-form-section">
            <div class="equipment-form-main">
              <label class="field">
                <div class="field-label">Название</div>
                <input
                  class="field-input"
                  value={st().title}
                  onInput={(e) => setSt({ ...st(), title: e.currentTarget.value })}
                  placeholder="Напр., DP 127 × 9.19, замок 4½ FH"
                  autofocus
                />
              </label>

              <label class="field">
                <div class="field-label">Описание</div>
                <textarea
                  class="field-input"
                  rows={3}
                  value={st().description}
                  onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                  placeholder="Произвольное примечание"
                />
              </label>

              <label class="field">
                <div class="field-label">
                  Бурильная компания
                  <span class="dim-hint" style={{ 'margin-left': 'var(--sp-2)' }}>
                    справочник, для группировки/поиска
                  </span>
                </div>
                <div class="row" style={{ gap: 'var(--sp-2)', 'align-items': 'center' }}>
                  <select
                    class="field-input"
                    style={{ flex: 1 }}
                    value={st().drillingCompanyId ?? ''}
                    onChange={(e) =>
                      setSt({
                        ...st(),
                        drillingCompanyId:
                          e.currentTarget.value === '' ? null : e.currentTarget.value,
                      })
                    }
                  >
                    <option value="">— без компании —</option>
                    <For each={props.companies}>
                      {(c) => <option value={c.id}>{c.name}</option>}
                    </For>
                  </select>
                  <button
                    class="btn"
                    type="button"
                    title="Создать новую компанию"
                    disabled={busy()}
                    onClick={async () => {
                      const name = prompt('Название новой бурильной компании')
                      if (!name) return
                      const trimmed = name.trim()
                      if (trimmed.length === 0) return
                      try {
                        const created = await props.onCreateCompany(trimmed)
                        setSt({ ...st(), drillingCompanyId: created.id })
                      } catch (e) {
                        setErr(`Не удалось создать компанию: ${String(e)}`)
                      }
                    }}
                  >
                    <Icon name="plus" /> Новая
                  </button>
                </div>
              </label>

              <label class="field">
                <div class="field-label">
                  Теги
                  <span class="dim-hint" style={{ 'margin-left': 'var(--sp-2)' }}>
                    для поиска/группировки; Enter — добавить
                  </span>
                </div>
                <Show when={st().tags.length > 0}>
                  <div class="row" style={{ gap: 'var(--sp-2)', 'flex-wrap': 'wrap', 'margin-bottom': 'var(--sp-2)' }}>
                    <For each={st().tags}>
                      {(t) => (
                        <span class="badge" style={{ display: 'inline-flex', 'align-items': 'center', gap: 'var(--sp-1)' }}>
                          {t}
                          <button
                            class="btn-icon btn-ghost"
                            type="button"
                            style={{ width: '16px', height: '16px' }}
                            title="Убрать тег"
                            onClick={() => removeTag(t)}
                          >
                            <Icon name="x" size={10} />
                          </button>
                        </span>
                      )}
                    </For>
                  </div>
                </Show>
                <input
                  class="field-input"
                  value={tagInput()}
                  onInput={(e) => setTagInput(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    // Enter и запятая — добавить тег (не сабмитить форму).
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault()
                      addTag()
                    }
                  }}
                  onBlur={() => addTag()}
                  placeholder="Напр., аренда, 215.9, склад-12"
                />
              </label>

              <Show when={st().source?.origin_state_uuid || st().source?.origin_well_uuid}>
                <div class="field-hint" style={{ color: 'var(--text-muted, inherit)' }}>
                  Провенанс (только чтение): импортирован
                  <Show when={st().source?.origin_state_uuid}>
                    {' '}из стейта <span class="mono">{st().source!.origin_state_uuid!.slice(0, 8)}…</span>
                  </Show>
                  <Show when={st().source?.origin_well_uuid}>
                    {' '}· well <span class="mono">{st().source!.origin_well_uuid!.slice(0, 8)}…</span>
                  </Show>
                </div>
              </Show>
            </div>

            <Show
              when={meta().fields.length > 0}
              fallback={<div class="equipment-no-dims">У этого типа нет размерных полей</div>}
            >
              <div class="field">
                <div class="field-label">Параметры</div>
                <div class="dim-grid equipment-dim-grid">
                  <For each={meta().fields}>
                    {(f) => (
                      <div class="dim-cell">
                        <div class="dim-label">
                          <span class="dim-symbol">{f.label}</span>
                          <Show when={f.hint || f.unit}>
                            <span class="dim-hint">
                              {f.hint ? `${f.hint} ` : ''}({f.unit})
                            </span>
                          </Show>
                        </div>
                        <input
                          class="field-input mono"
                          value={st().fieldStrs[f.key] ?? ''}
                          onInput={(e) => updateField(f.key, e.currentTarget.value)}
                          inputmode="decimal"
                          placeholder="0"
                        />
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            <Show when={meta().shape === 'tube_with_joins'}>
              <div class="field">
                <div class="field-label">Замки (joins)</div>
                <div class="dim-grid equipment-dim-grid">
                  <For each={Object.keys(st().joinsStrs)}>
                    {(k) => (
                      <div class="dim-cell">
                        <div class="dim-label">
                          <span class="dim-symbol">{k}</span>
                          <span class="dim-hint">({/_D\d$/.test(k) ? 'mm' : 'm'})</span>
                        </div>
                        <input
                          class="field-input mono"
                          value={st().joinsStrs[k] ?? ''}
                          onInput={(e) => updateJoin(k, e.currentTarget.value)}
                          inputmode="decimal"
                          placeholder="0"
                        />
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            <Show when={meta().shape === 'bit'}>
              <div class="field">
                <div class="field-label">Насадки (nozzles)</div>
                <div style={{ display: 'flex', 'flex-direction': 'column', gap: 'var(--sp-2)' }}>
                  <For each={st().nozzles}>
                    {(nz, i) => (
                      <div class="row" style={{ gap: 'var(--sp-2)', 'align-items': 'center' }}>
                        <span class="muted text-xs">#{i() + 1}</span>
                        <label style={{ display: 'flex', 'flex-direction': 'column', flex: 1 }}>
                          <span class="dim-hint">d (mm)</span>
                          <input
                            class="field-input mono"
                            value={nz.diam}
                            onInput={(e) => updateNozzle(i(), 'diam', e.currentTarget.value)}
                            inputmode="decimal"
                          />
                        </label>
                        <label style={{ display: 'flex', 'flex-direction': 'column', flex: 1 }}>
                          <span class="dim-hint">кол-во</span>
                          <input
                            class="field-input mono"
                            value={nz.quantity}
                            onInput={(e) => updateNozzle(i(), 'quantity', e.currentTarget.value)}
                            inputmode="numeric"
                          />
                        </label>
                        <label style={{ display: 'flex', 'flex-direction': 'column', flex: 1 }}>
                          <span class="dim-hint">μ</span>
                          <input
                            class="field-input mono"
                            value={nz.mu_coef}
                            onInput={(e) => updateNozzle(i(), 'mu_coef', e.currentTarget.value)}
                            inputmode="decimal"
                          />
                        </label>
                        <button class="btn-icon" type="button" title="Удалить" onClick={() => removeNozzle(i())}>
                          <Icon name="x" />
                        </button>
                      </div>
                    )}
                  </For>
                  <button class="btn" type="button" onClick={addNozzle}>
                    <Icon name="plus" /> Добавить насадку
                  </button>
                </div>
              </div>
            </Show>
          </section>
        </main>
      </div>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type ToolPageProps = {
  toolId?: string | null
  create?: boolean
  onOpen: (id: string) => void
  onCreate: () => void
  onBack: () => void
}

export const ToolPage: Component<ToolPageProps> = (props) => {
  const [items, { refetch }] = createResource(listTools)
  const [companies, { refetch: refetchCompanies }] = createResource(listDrillingCompanies)
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')
  const [filterKind, setFilterKind] = createSignal<string>('') // '' = все
  const [filterCompany, setFilterCompany] = createSignal<string>('') // '' = все

  // Map id → name для отображения в таблице.
  const companyName = (id: string | null): string => {
    if (id == null) return ''
    return (companies() ?? []).find((c) => c.id === id)?.name ?? ''
  }

  // Создание компании прямо из формы инструмента (передаётся в Editor).
  const onCreateCompany = async (name: string): Promise<DrillingCompanyDto> => {
    const created = await createDrillingCompany({ name })
    refetchCompanies()
    return created
  }

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor(TOOL_KINDS[0]!.id)
    if (props.toolId == null) return null
    const item = (items() ?? []).find((e) => e.id === props.toolId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<ToolDto[]>(() => {
    const all = items() ?? []
    const q = filterText().trim().toLowerCase()
    const k = filterKind()
    const c = filterCompany()
    return all.filter((t) => {
      if (k && t.kind !== k) return false
      if (c && (t.drilling_company_id ?? '') !== c) return false
      if (q.length === 0) return true
      const desc = (() => {
        const body = unwrapPayload(t.kind, t.payload)
        const d = body?.description
        return typeof d === 'string' ? d : ''
      })()
      return (
        t.title.toLowerCase().includes(q) ||
        desc.toLowerCase().includes(q) ||
        companyName(t.drilling_company_id).toLowerCase().includes(q)
      )
    })
  })

  const openCreate = () => props.onCreate()
  const openEdit = (t: ToolDto) => props.onOpen(t.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  const onDelete = async (t: ToolDto) => {
    if (!confirm(`Удалить «${t.title}»?`)) return
    setDelErr(null)
    try {
      await deleteTool(t.id)
      refetch()
    } catch (err) {
      if (err instanceof ToolInUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  // Краткое описание ключевых параметров строкой — для колонки таблицы.
  // Берём первые 3 поля meta (D/d/L и т.п.) с display-значениями.
  const fieldsSummary = (t: ToolDto): string => {
    const meta = KIND_BY_ID[t.kind]
    if (!meta || meta.fields.length === 0) return '—'
    const body = unwrapPayload(t.kind, t.payload)
    if (!body) return '—'
    return meta.fields
      .slice(0, 3)
      .map((f) => {
        const raw = body[f.key]
        const num = typeof raw === 'number' ? raw : 0
        return `${f.label}=${fromSi(num, f.unit).toFixed(f.unit === 'mm' ? 1 : 3)}`
      })
      .join(' · ')
  }

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог инструмента</h1>
          <p class="page-subtitle">
            Трубы, долота, переводники — что-то, из чего собираются компоновки
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Инструмент
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.length === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первую запись — трубу, долото или переводник
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить инструмент
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.length > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / описанию"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <select
            class="field-input"
            style={{ width: '220px' }}
            value={filterKind()}
            onChange={(e) => setFilterKind(e.currentTarget.value)}
          >
            <option value="">Все типы</option>
            <For each={TOOL_KINDS}>
              {(k) => <option value={k.id}>{k.label}</option>}
            </For>
          </select>
          <select
            class="field-input"
            style={{ width: '240px' }}
            value={filterCompany()}
            onChange={(e) => setFilterCompany(e.currentTarget.value)}
          >
            <option value="">Все компании</option>
            <For each={companies() ?? []}>
              {(c) => <option value={c.id}>{c.name}</option>}
            </For>
          </select>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.length}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th style={{ width: '180px' }}>Тип</th>
                <th>Название</th>
                <th style={{ width: '180px' }}>Компания</th>
                <th>Параметры</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(t) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(t)}>
                    <td class="mono text-xs muted" title={t.id}>#{t.id.slice(0, 8)}</td>
                    <td>
                      <div class="equipment-table-kind">
                        <Show when={KIND_BY_ID[t.kind]} keyed>
                          {(k) => (
                            <span class="equipment-table-kind-icon" aria-hidden="true">
                              <ToolKindGlyph kind={k} />
                            </span>
                          )}
                        </Show>
                        <span>{kindLabel(t.kind)}</span>
                      </div>
                    </td>
                    <td style={{ 'font-weight': 500 }}>{t.title}</td>
                    <td>
                      <Show when={companyName(t.drilling_company_id)} fallback={<span class="muted">—</span>}>
                        {companyName(t.drilling_company_id)}
                      </Show>
                    </td>
                    <td class="mono text-xs">{fieldsSummary(t)}</td>
                    <td>
                      <button
                        class="btn-icon"
                        title="Удалить"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(t)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor
            state={ed}
            companies={companies() ?? []}
            onClose={closeEditor}
            onSaved={onSaved}
            onCreateCompany={onCreateCompany}
          />
        </div>
      )}
    </Show>
  )
}
